<template>
  <div class="contest-quiz" :class="{ 'is-review': isReview, 'is-video-fullscreen': isVideoFullscreen }">
    <!-- 顶部信息栏 -->
    <div class="header-bar">
      <div class="progress-info">
        <span class="current">{{ this.title }} {{ currentQuestionIndex }}</span>/{{ questions.length }}
      </div>
      <div class="countdown" v-if="!isReview">
        <span class="auto-submit-tip" v-if="remainingTime <= 300 && remainingTime > 0">
          {{ remainingTime }}s后自动提交
        </span>
        <template v-else-if="remainingTime > 300">
          <img class="clock-icon" src="@/assets/images/contest/time.png" alt=""/>
          <span class="time-block">{{ formattedHours }}</span>
          <span class="time-colon">:</span>
          <span class="time-block">{{ formattedMinutes }}</span>
          <span class="time-colon">:</span>
          <span class="time-block">{{ formattedSeconds }}</span>
        </template>
      </div>
    </div>

    <!-- 题目列表 -->
    <div class="question-list-wrapper" ref="scrollContainer">
      <div class="question-list">
      <div
        class="question-item"
        v-for="(question) in questions"
        :key="question.id"
        :id="`question-${question.id}`"
      >
        <div class="question-header">
          <span class="question-number">第{{ numberToChinese(question.sort) }}题</span>
        </div>
        <div class="question-content">
          <div class="question-title">
            {{ question.questionTitle }}
            <!--            <span class="question-type">({{ question.questionType === '1' ? '单选题' : '多选题' }})</span>-->
          </div>

          <!-- 题目图片（支持多张，一排三张） -->
          <div class="question-media" v-if="question.questionImages && question.questionImages.length > 0">
            <div class="images-grid">
              <img
                v-for="(img, imgIndex) in question.questionImages"
                :key="imgIndex"
                :src="img.url"
                class="question-image"
                :class="{ 'single': question.questionImages.length === 1 }"
                @click="previewImages(question.questionImages, imgIndex)"
                alt=""
              >
            </div>
          </div>

          <!-- 题目视频 -->
          <div class="question-video" v-if="question.analysisVideo && question.analysisVideo.fileId">
            <TrainingCampVideoPlayer
              :playerVideo="question.analysisVideo"
              :enableTrace="false"
              @fullscreen-change="handleVideoFullscreenChange"
            />
          </div>
        </div>

        <!-- 选项区域 -->
        <div class="options-section">
          <div class="options-title">
            <span class="title-mark"></span>
            <span class="options-label">
              选择答案({{ question.questionType === '1' ? '单选题' : '多选题' }})
            </span>
            <span class="question-score" v-if="isReview && hasQuestionScore(question.id)">
              得分{{ questionScores[question.id] }}分
            </span>
          </div>
          <div class="options-list">
            <div
              class="option-item"
              v-for="option in question.questionOptions"
              :key="option.id"
              :class="{ selected: isOptionSelected(question.id, option.id) }"
              @click="handleOptionClick(question, option)"
            >
              <span class="option-radio"></span>
              <span class="option-text">{{ option.optionContent }}</span>
              <!-- 暂时注释：不显示正确答案对勾 -->
              <!-- <span
                class="option-meta"
                v-if="isReview && isOptionCorrect(question.id, option.id)"
              >
                <span class="option-correct-icon" v-if="isOptionCorrect(question.id, option.id)"></span>
              </span> -->
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- 底部提交按钮（非回顾模式显示） -->
    <div class="footer-bar" v-if="!isReview">
      <div
        class="submit-btn"
        @click="handleSubmit"
      >
        提交
      </div>
    </div>

  </div>
</template>

<script>
import {ImagePreview} from 'vant'
import {
  getExamDetail,
  submitExamAnswer,
  getExamRemainingTime,
  getUserSelectedAnswers,
  saveSelectedAnswer,
  markTheStartOfTheExam
} from '@/api/contest'
import {postRemind} from '@/api/simulator'
import TrainingCampVideoPlayer from '@/views/RTCZone/compontents/TrainingCampVideoPlayer.vue'
import withRouteTracking from '@/utils/withRouteTracking'
import {handleTraceFunction} from '@/utils/trace'
import {numberToChinese, canScroll} from '@/utils/util'

const UserProfile = {
  name: 'contest',
  components: {
    TrainingCampVideoPlayer
  },
  data() {
    return {
      contestId: '',
      questions: [],
      answers: {}, // { questionId: [optionIds] }
      correctOptions: {}, // { questionId: [optionIds] }
      questionScores: {}, // { questionId: score }
      remainingTime: 0,
      timer: null,
      isSubmitting: false,
      currentQuestionIndex: 1, // 当前滚动到的题目序号
      isReview: false, // 是否为回顾模式
      isAnswered: undefined, // 参赛人0-未参加 1-参加(已结束) 2:进行中
      title: '',
      isVideoFullscreen: false,
    }
  },
  computed: {
    userInfo() {
      return this.$store.getters.userInfo
    },
    formattedTime() {
      const hours = Math.floor(this.remainingTime / 3600)
      const minutes = Math.floor((this.remainingTime % 3600) / 60)
      const seconds = this.remainingTime % 60
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    formattedHours() {
      return String(Math.floor(this.remainingTime / 3600)).padStart(2, '0')
    },
    formattedMinutes() {
      return String(Math.floor((this.remainingTime % 3600) / 60)).padStart(2, '0')
    },
    formattedSeconds() {
      return String(this.remainingTime % 60).padStart(2, '0')
    },
    answeredCount() {
      return Object.keys(this.answers).filter(key => this.answers[key] && this.answers[key].length > 0).length
    },
    hasAnswered() {
      return Object.keys(this.answers).length > 0
    }
  },
  mounted() {
    this.contestId = this.$route.query.contestId
    this.isReview = this.$route.query.isReview === 'true'
    this.initContest()
    // 添加滚动监听（监听容器滚动）
    this.$nextTick(() => {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.addEventListener('scroll', this.handleScroll)
      }
    })
    // 添加页面可见性监听，用于从后台返回时重新获取剩余时间
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    this.clearTimers()
    // 移除滚动监听
    if (this.$refs.scrollContainer) {
      this.$refs.scrollContainer.removeEventListener('scroll', this.handleScroll)
    }
    // 移除页面可见性监听
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    // 确保恢复页面滚动（防止视频全屏时点击回退按钮导致滚动被禁用）
    canScroll()
    this.$tipsDialog.close()
  },
  methods: {
    numberToChinese,
    handleVideoFullscreenChange(isFullScreen) {
      this.isVideoFullscreen = !!isFullScreen
    },
    clearTimers() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    handleScroll() {
      const container = this.$refs.scrollContainer
      if (!container) return

      // 检查是否滚动到底部（允许一定误差）
      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10

      // 如果滚动到底部，直接显示最后一题
      if (isAtBottom && this.questions.length > 0) {
        this.currentQuestionIndex = this.questions.length
        return
      }

      // 遍历所有题目，找到当前可见的题目
      for (let i = 0; i < this.questions.length; i++) {
        const question = this.questions[i]
        const element = document.getElementById(`question-${question.id}`)
        if (element) {
          const rect = element.getBoundingClientRect()
          // 如果题目顶部在容器顶部以下100px内，说明这道题正在被查看
          if (rect.top <= 44 + 100) {
            this.currentQuestionIndex = i + 1
          }
        }
      }
    },
    async initContest() {
      try {
        // 员工不能参加竞赛（memberType === 0 表示术者）
        if (this.userInfo.memberType !== 0) {
          this.$tipsDialog.alert({
            message: '仅术者可参加竞赛',
            confirmButtonText: '我知道了',
            // decorationImage: require('@/assets/images/decorationImage.png'),
          }).finally(() => {
            this.$router.replace('/home')
          })
          return
        }

        // 获取比赛状态（回顾模式和非回顾模式都需要）
        const userId = this.userInfo.id
        const {code, data} = await getExamRemainingTime(this.contestId)
        if (code === 0 && data) {
          const examStatus = data.examStatus // 竞赛0未开始 1进行中 2已结束
          const isAnswered = data.isAnswered // 参赛人0-未参加 1-参加(已结束) 2:进行中
          this.isAnswered = isAnswered
          this.title = data.title
          const startDateLabel = this.formatStartDate(data.startTime)
          if (this.isReview) {
            // 回顾模式：如果不是已完成状态（isAnswered !== 1），跳转到首页
            if (isAnswered !== 1) {
              this.$router.replace('/home')
              return
            }
          } else {
            // 非回顾模式：检查比赛状态
            let errorMsg = ''
            if (examStatus === 0) {
              errorMsg = startDateLabel ? `比赛还未开始，请${startDateLabel}再来哦` : '比赛还未开始，请稍后再来哦'
            } else if (examStatus === 2) {
              errorMsg = '比赛已结束，感谢关注，下次再来哦！'
            }

            if (errorMsg) {
              this.$tipsDialog.alert({
                message: errorMsg,
                confirmButtonText: '我知道了',
                // decorationImage: require('@/assets/images/decorationImage.png'),
              }).finally(() => {
                this.$router.replace('/home')
              })
              return
            }
            // 如果已答题，直接跳转到结果页
            if (isAnswered === 1) {
              this.$router.replace({
                path: '/contestResult',
                query: {contestId: this.contestId}
              })
              return
            }

            // 首次进入弹窗提示
            if (isAnswered === 0) {
              const iconUrl1 = require('@/assets/images/contest/time.png')
              const iconUrl2 = require('@/assets/images/answerQuestions.png')
              const iconUrl3 = require('@/assets/images/fraction.png')
              const iconUrl4 = require('@/assets/images/popUpNotificationIcon1.png')
              const confirmed = await this.$tipsDialog.confirm({
                message: `
            <div>
            <div class="f-w-bold m-b-10">尊敬的${this.title}-海选参赛者:</div>
            <div class="f-w-400 m-b-12">您好! 感谢您报名${this.title}，${this.title}面向${this.title === '青锋赛' ? '非' : ''}房颤独立术者，海选最终晋级名单<span class="f-w-bold">将于7月初</span>公布，请仔细阅读以下规则:</div>
            <div class="f-s-14 f-w-400 m-b-16">
            <div class="m-b-6"><img src="${iconUrl1}" class="remind-icon m-r-8"/><span>答题时间:30 分钟</div>
            <div class="m-b-6"><img src="${iconUrl2}" class="remind-icon m-r-8"/><span>题量:20题</span></div>
            <div><img src="${iconUrl3}" class="remind-icon m-r-8"/>满分:100分，每人仅1次机会</div>
            </div>
            <div class="f-w-bold">评分:</div>
            <div class="f-s-14 f-w-400 m-b-8">
            <div><span>· 单选5分/题</div>
            <div><span>· 多选题:全对5分，漏选3分，错选0分</span></div>
            </div>
            <div class="f-w-bold">提示:</div>
            <div class="f-s-14 f-w-400 m-b-18">
            <div class="m-b-6"><span>· 答题期间不可退出，离开即按已答记录自动提交</div>
            </div>
            <div class="f-w-bold m-b-8"><img src="${iconUrl4}" class="remind-icon"/>确认无误后请点击"开始答题"，预祝斩获佳绩!</div>
            </div>
            `,
                title: `${this.title}-赛前须知`,
                confirmButtonText: '确认',
                cancelButtonText: '取消',
                messageStyle: {
                  textAlign: 'left',
                },
                decorationImage: require('@/assets/images/decorationImage.png'),
              }).then(() => {
                handleTraceFunction({functionName: `${this.title}-赛前须知-确认`, createTime: new Date().getTime()})
                return true
              }).catch(() => false)

              if (!confirmed) {
                this.$router.replace('/home')
                return
              }
              // 给后端标记用户考试开始
              markTheStartOfTheExam(this.contestId)
              // 用户确认后，记录已提醒状态
              await postRemind({
                readType: 'contestFirstEnter' + this.contestId,
                readTypeName: '竞赛首次进入提示',
                iepUserId: userId
              })
            }
          }
        }

        const res = await getExamDetail(this.contestId)
        const examDetailData = res.data

        // 转换后端数据格式为前端需要的格式
        this.questions = (examDetailData.questionDetails || []).map((q, index) => ({
          id: q.id,
          sort: q.sort || index + 1, // 使用序号作为题目编号
          questionTitle: q.questionTitle,
          questionType: q.questionType, // '1'-单选 '2'-多选
          // 将单张图片转换为数组格式
          questionImages: q.questionImage ? [{url: q.questionImage.url}] : [],
          // 视频信息（使用 analysisVideo）
          analysisVideo: q.analysisVideo || null,
          questionOptions: q.questionOptions || []
        }))

        // 回顾模式加载用户答案，非回顾模式获取剩余时间并启动倒计时
        await (this.isReview ? this.loadUserSelectedAnswers('获取答题记录失败', 2) : this.fetchRemainingTime())
      } catch (error) {
        if (error.code === 30002) {
          this.$toast({
            message: error.message || error.msg || '竞赛未发布',
            duration: 1500,
            onClose: () => {
              this.$router.replace('/home')
            }
          })
          return
        }
        this.$toast(error.message || error.msg || '获取竞赛信息失败')
      }
    },
    startCountdown() {
      this.clearTimers()
      this.timer = setInterval(() => {
        this.remainingTime--
        if (this.remainingTime <= 0) {
          this.autoSubmit()
        }
      }, 1000)
    },
    autoSubmit() {
      this.clearTimers()
      this.doSubmit()
    },
    isOptionSelected(questionId, optionId) {
      const selected = this.answers[questionId] || []
      return selected.includes(optionId)
    },
    isOptionCorrect(questionId, optionId) {
      const correct = this.correctOptions[questionId] || []
      return correct.includes(optionId)
    },
    hasQuestionScore(questionId) {
      return Object.prototype.hasOwnProperty.call(this.questionScores, questionId)
    },
    handleOptionClick(question, option) {
      // 回顾模式下不可点击
      if (this.isReview) return

      let newOptionIds = []
      if (question.questionType === '1') {
        // 单选
        newOptionIds = [option.id]
        this.$set(this.answers, question.id, newOptionIds)
      } else {
        // 多选
        const current = this.answers[question.id] ? [...this.answers[question.id]] : []
        const index = current.indexOf(option.id)
        if (index > -1) {
          current.splice(index, 1)
        } else {
          current.push(option.id)
        }
        newOptionIds = current
        this.$set(this.answers, question.id, newOptionIds)
      }

      // 实时保存选中的答案到后端
      this.saveAnswer(question.id, newOptionIds)
    },
    // 保存选中的答案到后端（用于断点续答）
    async saveAnswer(questionId, optionIds) {
      try {
        await saveSelectedAnswer({
          examId: this.contestId,
          questionId: questionId,
          optionIds: optionIds
        })
      } catch (error) {
        console.error('保存答案失败', error)
      }
    },
    handleSubmit() {
      if (this.isSubmitting) return

      // 1. 多选题校验：检查已作答的多选题是否至少选择2个
      const invalidMultiple = this.questions.find(q =>
        q.questionType === '2' &&
        this.answers[q.id] &&
        this.answers[q.id].length > 0 &&
        this.answers[q.id].length < 2
      )
      if (invalidMultiple) {
        this.$toast(`题目${invalidMultiple.sort}为多选题，请至少选择2个答案。`)
        // 滚动到对应题目
        this.scrollToQuestion(invalidMultiple.id)
        return
      }

      // 2. 检查未答题
      const unanswered = this.questions.filter(q => !this.answers[q.id] || this.answers[q.id].length === 0)

      let message = '确认提交吗？'
      if (unanswered.length > 0) {
        const unansweredNumbers = unanswered.map(q => q.sort).join('、')
        message = `您还有题目${unansweredNumbers}未答，确认提交吗？`
      }

      this.$tipsDialog.confirm({
        message,
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        decorationImage: require('@/assets/images/decorationImage.png'),
      }).then(() => {
        // 埋点：确认提交
        handleTraceFunction({functionName: '竞赛答题-提交', createTime: new Date().getTime()})
        this.doSubmit()
      }).catch(() => {
        // 取消提交
      })
    },
    async doSubmit() {
      if (this.isSubmitting) return
      this.isSubmitting = true
      this.clearTimers()

      try {
        // 构造提交数据：每个选项对应一个对象
        // 多选题会有多个对象，单选题只有一个对象
        const answerList = []
        Object.keys(this.answers).forEach(questionId => {
          const optionIds = this.answers[questionId] || []
          optionIds.forEach(optionId => {
            answerList.push({
              userId: this.userInfo.id,
              optionsId: optionId,
              questionId: questionId,
              examId: this.contestId
            })
          })
        })

        // 调用提交API
        const res = await submitExamAnswer(this.contestId, answerList)
        const {code} = res
        if (code === 0) {
          // 跳转到结果页
          this.$router.replace({
            path: '/contestResult',
            query: {
              contestId: this.contestId
            }
          })
        }
      } catch (error) {
        this.isSubmitting = false
        if (error.code === 30001) {
          // 考试已结束，提示用户稍后查看成绩
          this.$toast({
            message: '考试结束，请在三分钟后查看成绩',
            duration: 3000,
            onClose: () => {
              this.$router.replace('/home')
            }
          })
          return
        }
        this.$toast(error.message || error.msg || '提交失败，请重试')
        this.fetchRemainingTime()
      }
    },
    scrollToQuestion(questionId) {
      const element = document.getElementById(`question-${questionId}`)
      if (element) {
        element.scrollIntoView({behavior: 'smooth', block: 'center'})
      }
    },
    previewImages(images, startIndex = 0) {
      const urls = images.map(img => img.url)
      ImagePreview({
        images: urls,
        startPosition: startIndex,
        showIndex: true,
        closeable: true
      })
    },
    // 获取考试剩余时间
    async fetchRemainingTime() {
      try {
        const {code, data} = await getExamRemainingTime(this.contestId)
        if (code === 0) {
          const remainingTime = data && data.remainingTime
          const isAnswered = data && data.isAnswered
          this.isAnswered = isAnswered
          // 如果已答题，直接跳转到结果页
          if (isAnswered === 1) {
            this.remainingTime = 0
            this.clearTimers()
            this.$router.replace({
              path: '/contestResult',
              query: {contestId: this.contestId}
            })
            return
          }
          // ② 非进行中：直接结束（兜底）
          if (isAnswered !== 2) {
            this.clearTimers()
            return
          }

          // 如果剩余时间为0或负数，触发自动提交
          if (remainingTime <= 0) {
            this.remainingTime = 0
            await this.loadUserSelectedAnswers('获取已选答案失败', 1)
            this.autoSubmit()
            return
          }

          // 获取已选答案进行回显
          await this.loadUserSelectedAnswers('获取已选答案失败', 1)

          this.remainingTime = remainingTime
          // 重新启动倒计时
          this.startCountdown()
        }
      } catch (error) {
        this.$toast(error.message || error.msg || '获取剩余时间失败')
      }
    },
    // 获取用户已选择的答案
    async loadUserSelectedAnswers(errorMessage, type) {
      try {
        const {code, data} = await getUserSelectedAnswers(this.contestId, type)
        if (code === 0 && data) {
          // 将答题记录转换为 answers 格式: { questionId: [optionIds] }
          const answers = {}
          const correctOptions = {}
          const questionScores = {}
          const answerList = data || []
          answerList.forEach(item => {
            answers[item.questionId] = item.optionIds || []
            if (type === 2) {
              if (Array.isArray(item.correctOptionIds)) {
                correctOptions[item.questionId] = item.correctOptionIds
              }
              if (item.score !== undefined && item.score !== null) {
                questionScores[item.questionId] = item.score
              }
            }
          })
          this.answers = answers
          if (type === 2) {
            this.correctOptions = correctOptions
            this.questionScores = questionScores
          } else {
            this.correctOptions = {}
            this.questionScores = {}
          }
        }
      } catch (error) {
        this.$toast(error.message || error.msg || errorMessage)
      }
    },
    // 处理页面可见性变化（从后台返回时重新获取剩余时间）
    handleVisibilityChange() {
      // 页面变为可见时，且非回顾模式
      if (document.visibilityState === 'visible' && !this.isReview && !this.isSubmitting) {
        this.fetchRemainingTime()
      }
    },
    formatStartDate(dateStr) {
      if (!dateStr) return ''
      const datePart = dateStr.split(' ')[0]
      const parts = datePart.split('-')
      if (parts.length < 3) return ''
      const month = Number(parts[1])
      const day = Number(parts[2])
      if (!month || !day) return ''
      return `${month}月${day}日`
    },
  }
}

export default withRouteTracking(UserProfile)
</script>

<style lang="less" scoped>
.contest-quiz {
  height: 100vh;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.is-video-fullscreen {
    .header-bar,
    .footer-bar {
      display: none;
    }
  }
}

.header-bar {
  flex-shrink: 0;
  height: 44px;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  box-sizing: border-box;
  border-bottom: 1px solid #f1f1f1;

  .progress-info {
    font-size: 16px;
    font-weight: 600;
    color: #333;

    .current {
      color: #EB1700;
    }
  }

  .countdown {
    display: flex;
    align-items: center;

    .auto-submit-tip {
      font-size: 14px;
      font-weight: 600;
      color: #EB1700;
      background-color: #fff5f5;
      padding: 4px 12px;
      border-radius: 4px;
      animation: blink 1s ease-in-out infinite;
    }

    @keyframes blink {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.6;
      }
    }

    .clock-icon {
      width: 20px;
      height: 20px;
      margin-right: 8px;
    }

    .time-block {
      display: inline-block;
      width: 18px;
      height: 18px;
      line-height: 18px;
      text-align: center;
      font-size: 11px;
      font-weight: 500;
      color: #fff;
      background-color: #FF9090;
      border-radius: 4px;
    }

    .time-colon {
      font-size: 11px;
      font-weight: 500;
      color: #FF9090;
      margin: 0 2px;
    }
  }
}

.question-list-wrapper {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.question-list {
  padding: 16px 15px 20px;
}

.question-item {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f1f1f1;

  &:last-child {
    border-bottom: none;
  }

  .question-header {
    margin-bottom: 12px;

    .question-number {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }

  .question-content {
    .question-title {
      font-size: 15px;
      color: #000;
      margin-bottom: 12px;
      font-weight: 400;
      font-family: PingFang HK, PingFang HK;

      .question-type {
        color: #000;
        font-size: 15px;
      }
    }

    .question-media {
      margin-bottom: 12px;

      .images-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 10px;

        .question-image {
          width: calc((100% - 16px) / 3);
          height: auto;
          aspect-ratio: 1;
          object-fit: cover;
          display: block;
          border-radius: 4px;

          &.single {
            width: 100%;
            aspect-ratio: auto;
            border-radius: 0;
          }
        }
      }
    }

    .question-video {
      margin-top: 10px;
      margin-bottom: 12px;
    }
  }

  .options-section {
    .options-title {
      font-size: 15px;
      color: #000;
      font-weight: 500;
      padding-left: 8px;
      box-sizing: border-box;
      line-height: 20px;
      position: relative;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      font-family: PingFang HK, PingFang HK;

      .title-mark {
        position: absolute;
        width: 4px;
        left: 0;
        top: 4px;
        height: 12px;
        border-radius: 100px;
        background-color: #EB1700;
      }

      .options-label {
        padding-right: 8px;
      }

      .question-score {
        margin-left: auto;
        font-size: 12px;
        color: #22b168;
        font-weight: 500;
        white-space: nowrap;
      }
    }

    .options-list {
      .option-item {
        width: 100%;
        margin-bottom: 12px;
        background-color: #F3F3F3;
        border-radius: 30px;
        font-size: 14px;
        color: #333333;
        padding: 10px 12px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        transition: all 0.2s;
        border: 1px solid transparent;

        .option-radio {
          width: 14px;
          height: 14px;
          border: 1px solid #C1BBB3;
          border-radius: 50%;
          margin-right: 10px;
          flex-shrink: 0;
          box-sizing: border-box;
        }

        &.selected {
          color: #EB1700;
          background-color: #fff5f5;
          border-color: #EB1700;

          .option-radio {
            border: none;
            background: url('~@/assets/images/active.png') no-repeat center center;
            background-size: contain;
          }
        }

        .option-text {
          flex: 1;
          line-height: 20px;
        }

        .option-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-left: 8px;
          flex-shrink: 0;
        }

        .option-correct-icon {
          position: relative;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1px solid #22b168;
          box-sizing: border-box;
          flex-shrink: 0;

          &::after {
            content: '';
            position: absolute;
            left: 4px;
            top: 1px;
            width: 5px;
            height: 9px;
            border: solid #22b168;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
          }
        }
      }
    }
  }
}

.footer-bar {
  flex-shrink: 0;
  background-color: #fff;
  padding: 12px 15px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  border-top: 1px solid #f1f1f1;

  .submit-btn {
    width: 100%;
    height: 42px;
    background-color: #EB1700;
    border-radius: 35px;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/contest/index.vue